/**
 * Derives what a YouTube link can tell us on its own: the video id, and from
 * it the thumbnail. Nothing here calls YouTube — the id is parsed out of the
 * URL and the thumbnail URL is built from it, so previews render without an
 * API key.
 *
 * Aspect ratio is derivable, but not from the URL — it takes measuring a
 * thumbnail, so it lives in {@link YoutubeFormatHelper}. Duration, encoding and
 * file size stay underivable; the author gets a disclaimer for those instead.
 */
export class YoutubeHelper {
  /** A YouTube video id is always 11 URL-safe characters. */
  private static readonly VIDEO_ID = /^[A-Za-z0-9_-]{11}$/;

  /**
   * Pulls the video id out of any link shape YouTube hands out (watch,
   * youtu.be, shorts, embed, live), with or without a scheme and with any
   * trailing params. Returns null for anything else — that null means "no
   * thumbnail", never "invalid, block the author".
   */
  static extractVideoId(rawUrl: string): string | null {
    const trimmed = rawUrl.trim();
    if (!trimmed) {
      return null;
    }

    let url: URL;
    try {
      url = new URL(/^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`);
    } catch {
      return null;
    }

    const host = url.hostname.toLowerCase().replace(/^(www|m)\./, '');
    const segments = url.pathname.replace(/^\/+/, '').split('/');

    if (host === 'youtu.be') {
      return this.asVideoId(segments[0]);
    }

    if (host !== 'youtube.com' && host !== 'music.youtube.com') {
      return null;
    }

    if (segments[0] === 'watch') {
      return this.asVideoId(url.searchParams.get('v'));
    }

    if (['shorts', 'embed', 'live', 'v'].includes(segments[0] ?? '')) {
      return this.asVideoId(segments[1]);
    }

    return null;
  }

  /**
   * Thumbnail for a video id. `hqdefault` rather than `maxresdefault`, which
   * 404s on older and lower-resolution uploads.
   */
  static thumbnailUrl(videoId: string): string {
    return `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
  }

  private static asVideoId(value: string | null | undefined): string | null {
    return value && this.VIDEO_ID.test(value) ? value : null;
  }
}
